import { H3, H4, P } from '@/components/common/Typo'
import useAppContext from '@/hooks/useAppContext'
import { getSelectedVariantInfo } from '@/utils/product'
import { useTranslate } from '@tolgee/react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ChevronDownIcon,
  ChevronUpIcon,
  cn,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  RadioGroup,
  RadioGroupItem,
} from 'oddle-shop-monorepo.design-system'
import { ReactNode, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon,
} from 'src/components/icons/NutriGrade'
import useCustomisation from 'src/hooks/useCustomisation'
import { formatDiff } from 'src/utils/number'
import { getAllProductNutrigradeTags } from 'src/utils/product'
import ProductOption from './ProductOption'

const VariantHeader = ({
  show = true,
  selectedVariant,
  selectedVarPriceDiff = 0,
  hideArrow = false,
  variantPriceDisplay = 'priceDifference',
}: {
  show?: boolean
  selectedVariant?: Oms.Variant
  selectedVarPriceDiff?: number
  hideArrow?: boolean
  variantPriceDisplay?: string
}) => {
  const { t } = useTranslate()
  const { formatMoney } = useAppContext()

  const getVariantPrice = ({
    selectedVariant,
  }: {
    selectedVariant?: Oms.Variant
  }) => {
    if (variantPriceDisplay === 'priceDifference') {
      return selectedVarPriceDiff <= 0
        ? ''
        : formatDiff({ value: selectedVarPriceDiff, formatter: formatMoney })
    } else {
      return formatMoney(selectedVariant?.price || 0)
    }
  }

  return (
    <div className="flex w-full cursor-pointer justify-between rounded-lg bg-[#F6F6F6] p-2.5 lg:p-5 lg:py-2.5">
      <div className="flex w-fit flex-col">
        <H3 className="m-0 text-[#393939]">{t('Choose from below')}</H3>
        <P className={cn('m-0', 'text-text-default')}>{t('Select 1')}</P>
      </div>
      <div className="flex items-center justify-end">
        <div className="flex flex-col items-end">
          <P className={cn('m-0', 'text-text-default')} textAlign="right">
            {selectedVariant?.variantName || ''}
          </P>
          <P
            className={cn('m-0', 'text-text-default')}
            data-testid="text.bodySubText"
          >
            {getVariantPrice({ selectedVariant })}
          </P>
        </div>
        {!hideArrow && (
          <div className="flex">
            {show ? (
              <ChevronUpIcon className="text-3xl" />
            ) : (
              <ChevronDownIcon className="text-3xl" />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface ProductVariantProps {
  variants: Oms.Variant[]
  id: string
  selectedVariantId?: string | null
  calculateOptionDiff: (optionId: string, variantId: string) => number
  hideArrow: boolean
  calculateVariantDiff: (variantId: string) => number
}

const VariantContent = ({
  show = true,
  variants = [],
  formatMoney = (x) => x.toString(),
  calculateVariantDiff = (x) => 0,
  variantPriceDisplay = 'priceDifference',
  name = 'selectedVariant',
}: {
  show?: boolean
  variants?: Oms.Variant[]
  formatMoney?: (x: number) => string
  calculateVariantDiff?: (x: string) => number
  variantPriceDisplay?: string
  name?: string
}) => {
  const { control } = useFormContext()

  const getVariantOptionPrice = (variant: Oms.Variant) => {
    if (variantPriceDisplay === 'priceDifference') {
      const variantDiff = calculateVariantDiff(variant.id)
      return variantDiff <= 0
        ? ''
        : formatDiff({ value: variantDiff, formatter: formatMoney })
    } else {
      return formatMoney(variant.price as number)
    }
  }

  if (!show) return null

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem id={name}>
          <FormControl>
            <RadioGroup
              defaultValue={field.value || variants?.[0]?.id}
              onValueChange={field.onChange}
              className="flex flex-col gap-2"
            >
              {variants.map((variant, idx) => {
                const existNutriGradeTag = checkExistNutriGradeTag(variant.tags)
                const variantNutrigradeTags = getAllProductNutrigradeTags(
                  variant.tags
                )
                return (
                  <FormItem
                    key={variant.id}
                    className={cn(
                      'flex w-full flex-row items-center justify-between gap-0',
                      idx + 1 < variants.length
                        ? 'border-lines-and-borders border-b'
                        : ''
                    )}
                  >
                    <FormLabel className="w-full cursor-pointer px-2.5 py-3.5">
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-center gap-2">
                          {((variantNutrigradeTags || []).length > 0 ||
                            existNutriGradeTag) && (
                            <div className="flex flex-nowrap md:flex-wrap">
                              <div className="m-auto p-2.5">
                                <NutriGradeIcon
                                  tags={variant.tags}
                                  height={40}
                                  width={89}
                                />
                              </div>
                              {(variantNutrigradeTags || []).length > 0 && (
                                <div className="pr-2.5">
                                  <div className="flex flex-wrap gap-[5px]">
                                    {variantNutrigradeTags?.map((tag) => (
                                      <div
                                        className="inline-block rounded-lg border border-solid border-[#bbb] bg-white px-[5px] py-1 text-sm text-black"
                                        key={tag}
                                      >
                                        {tag}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="relative flex-col items-start self-center">
                            <H4 className="font-medium" margin="none">
                              {variant.variantName}
                            </H4>
                            {getVariantOptionPrice(variant) && (
                              <H4
                                className="break-all"
                                marginTop="extraSmall"
                                marginBottom="none"
                                textAlign="left"
                                data-testid={`text.amount.${idx}`}
                                bold
                              >
                                {getVariantOptionPrice(variant)}
                              </H4>
                            )}
                          </div>
                        </div>
                      </div>
                    </FormLabel>
                    <FormControl>
                      <RadioGroupItem
                        value={variant.id}
                        className={cn(
                          'border-lines-and-borders data-[state=checked]:border-button [&>[data-state=checked]>svg]:fill-button flex cursor-pointer items-start gap-4 rounded-full border-2 p-2.5'
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )
              })}
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  )
}

const ProductVariant = ({
  variants,
  id,
  selectedVariantId,
  calculateVariantDiff,
  hideArrow,
  calculateOptionDiff,
}: ProductVariantProps): ReactNode => {
  // Add state to control Accordion open/close (must be before any return)
  const [openAccordion, setOpenAccordion] = useState<string>(id)
  const { getSectionFromId } = useCustomisation()
  const { formatMoney } = useAppContext()

  // State to control which ProductOption is open
  const [openOptionId, setOpenOptionId] = useState<string | undefined>(
    undefined
  )

  if (!variants || variants.length <= 1) return null
  const { selectedVariant, selectedVarPriceDiff } = getSelectedVariantInfo(
    variants,
    selectedVariantId
  )
  const { settings = {} } = getSectionFromId('productDetail')
  const { variantPriceDisplay } = settings as {
    variantPriceDisplay: string
  }

  return (
    <>
      <Accordion
        type="single"
        collapsible
        value={openAccordion}
        onValueChange={(v) => setOpenAccordion(v || '')}
        id={id}
      >
        <AccordionItem value={id} className="border-b-0">
          <AccordionTrigger className="outline-none hover:no-underline">
            <VariantHeader
              show={openAccordion === id}
              hideArrow={hideArrow}
              selectedVariant={selectedVariant}
              selectedVarPriceDiff={selectedVarPriceDiff}
              variantPriceDisplay={variantPriceDisplay}
            />
          </AccordionTrigger>
          <AccordionContent>
            <VariantContent
              variants={variants}
              formatMoney={formatMoney}
              calculateVariantDiff={calculateVariantDiff}
              variantPriceDisplay={variantPriceDisplay}
              name="selectedVariant"
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {(selectedVariant.subitems?.length || 0) > 0 && (
        <Accordion
          type="single"
          collapsible
          value={openOptionId}
          onValueChange={setOpenOptionId}
        >
          {selectedVariant.subitems &&
            selectedVariant.subitems.map((option) => (
              <ProductOption
                key={option.id}
                option={option}
                hideArrow={option.variants.length <= 1}
                calculateOptionDiff={calculateOptionDiff}
                open={openOptionId === option.id}
              />
            ))}
        </Accordion>
      )}
    </>
  )
}

export default ProductVariant
